import { useEffect, useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useToast } from '@chakra-ui/react';
import { useQuery } from 'hooks/useCustomQuery';
import { useQueryClient, useMutation } from '@tanstack/react-query';

import FormBase from '@/components/forms/new/FormBase';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    LoadingIndicator,
} from '@/components/v4';

// Form field components
import AzureNameField from './formFields/AzureNameField';
import AzureRegionsField from './formFields/AzureRegionsField';
import AzureChannelCountField from './formFields/AzureChannelCountField';

import { multiSelectDataMapper } from 'utils/utils';

// API
import createACSService from 'API/ProvisionedServices/createACSService';
import updateACSService from 'API/ProvisionedServices/updateACSService';
import getService from 'API/Service/getService';
import getACSRegions from 'API/ProvisionedServices/getACSRegions';

const DEFAULT_DATA = {
    name: 'ACS Direct Routing',
    maxChannel: 2,
    regions: [],
    voiceRoutes: [],
};

/**
 * Azure Service Profile page
 * @param {Object} props
 * @param {'ADD' | 'EDIT'} props.mode - decide whether to add or edit an ACS service
 * @returns
 */
export default function EditAzureService({ mode = 'ADD' }) {
    const navigate = useNavigate();
    const toast = useToast();
    const queryClient = useQueryClient();
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [regionsEnum, setRegionsEnum] = useState([]);

    const companyID = useSelector((state) => state.navigationLists.currentCompany);
    const { darkMode } = useSelector((state) => state.settings);

    const { id: dataID } = useParams();

    // Initial data loading
    const [initialValues, setInitialValues] = useState({
        ...DEFAULT_DATA,
        companyID,
    });

    const handleBackButton = () => navigate('/dashboard-services');

    // Load regions data
    const { data: regionsData } = useQuery({
        queryKey: ['acsRegions', companyID],
        queryFn: async () => {
            const res = await getACSRegions(companyID);
            return res.data.map(multiSelectDataMapper);
        },
        onSuccess: (data) => {
            setRegionsEnum(data);
        },
        refetchOnWindowFocus: false,
    });

    // Load service data for edit mode
    const { data: serviceData, isLoading: isLoadingService } = useQuery({
        queryKey: ['acsService', { id: dataID }],
        queryFn: async () => {
            const res = await getService(dataID);
            return res.data;
        },
        enabled: !!dataID && mode === 'EDIT',
        refetchOnWindowFocus: false,
    });

    // Update initial values when service data is loaded
    useEffect(() => {
        if (serviceData) {
            const regions = serviceData.regions.map(multiSelectDataMapper);
            setInitialValues({
                ...serviceData,
                regions,
            });
        }
    }, [serviceData]);

    // Update regions enum when regions data is loaded
    useEffect(() => {
        if (regionsData) {
            setRegionsEnum(regionsData);
        }
    }, [regionsData]);

    const createMutation = useMutation({
        mutationFn: (payload) => createACSService(payload),
        onSuccess: () => {
            toast({
                title: 'Success!',
                description: 'ACS Service has been created.',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            queryClient.invalidateQueries(['acsService']);
            navigate('/dashboard-services');
        },
        onError: (err) => {
            setError(err);
        },
    });

    const updateMutation = useMutation({
        mutationFn: (payload) => updateACSService(payload),
        onSuccess: () => {
            toast({
                title: 'Success!',
                description: 'ACS Service has been updated.',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            queryClient.invalidateQueries(['acsService', { id: dataID }]);
            queryClient.invalidateQueries(['acsService']);
            navigate('/dashboard-services');
        },
        onError: (err) => {
            setError(err);
        },
    });

    const handleSubmit = async (values) => {
        const payload = {
            ...values,
            companyID,
            maxChannel: Number(values.maxChannel),
        };

        if (mode === 'EDIT') {
            payload.id = dataID;
        }

        setIsLoading(true);
        try {
            if (mode === 'EDIT') {
                updateMutation.mutate(payload);
            } else {
                createMutation.mutate(payload);
            }
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const breadCrumbTrail = [
        { label: 'Services', path: ['/dashboard-services', { index: 0 }] },
        { label: `${mode === 'ADD' ? 'Add' : 'Edit'} ACS Service`, path: '' },
    ];

    // Check access for edit mode
    if (!dataID && mode === 'EDIT') {
        return <Navigate to="/dashboard-services" />;
    }

    // Show loading spinner for edit mode when loading service data
    if (mode === 'EDIT' && isLoadingService) {
        return <LoadingIndicator />;
    }

    return (
        <FormBase
            breadCrumbTrail={breadCrumbTrail}
            header={`${mode === 'ADD' ? 'Add' : 'Editing'} ACS Service`}
            initialValues={initialValues}
            finalFormSubmit={handleSubmit}
            customClick={true}
            onBack={handleBackButton}
            enableReinitialize={true}
            isSubmitDisabled={isLoading}
        >
            {({ errors, touched }) => [
                <AzureNameField 
                    key="name"
                    darkMode={darkMode}
                    errors={errors}
                    touched={touched}
                />,
                <AzureRegionsField 
                    key="regions"
                    darkMode={darkMode}
                    errors={errors}
                    touched={touched}
                    regionsEnum={regionsEnum}
                />,
                <AzureChannelCountField 
                    key="maxChannel"
                    darkMode={darkMode}
                    errors={errors}
                    touched={touched}
                />,
                error && (
                    <Alert key="error" status="error" sx={{ marginTop: '1rem' }}>
                        <AlertIcon />
                        <AlertDescription>
                            {error?.response?.data?.message ||
                                'An error occurred. Please try again later.'}
                        </AlertDescription>
                    </Alert>
                )
            ].filter(Boolean)}
        </FormBase>
    );
}
